import React, { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { appThemes } from '~/theme';

import { CssBaseline, ThemeProvider, useMediaQuery } from '@mui/material';
import type { Theme } from '@mui/material/styles';

export const THEME_MODE_STORAGE_KEY = 'adminThemeMode';

export const THEME_MODES = {
  SYSTEM: 'system',
  LIGHT: 'light',
  DARK: 'dark',
} as const;

export type ThemeMode = (typeof THEME_MODES)[keyof typeof THEME_MODES];

type ResolvedThemeMode = typeof THEME_MODES.LIGHT | typeof THEME_MODES.DARK;

const DEFAULT_THEME_MODE = THEME_MODES.SYSTEM;

interface ThemeModePreferenceContextValue {
  themeMode: ThemeMode;
  resolvedThemeMode: ResolvedThemeMode;
  setThemeMode: (themeMode: ThemeMode) => void;
}

interface ThemeModePreferenceProviderProps {
  children: ReactNode;
  themes?: Record<ResolvedThemeMode, Theme>;
}

const ThemeModePreferenceContext = createContext<ThemeModePreferenceContextValue>({
  themeMode: DEFAULT_THEME_MODE,
  resolvedThemeMode: THEME_MODES.LIGHT,
  setThemeMode: () => undefined,
});

export function isThemeMode(value: unknown): value is ThemeMode {
  return value === THEME_MODES.SYSTEM || value === THEME_MODES.LIGHT || value === THEME_MODES.DARK;
}

function readStoredThemeMode(): ThemeMode {
  try {
    const item = window.localStorage.getItem(THEME_MODE_STORAGE_KEY);
    const parsed = item ? JSON.parse(item) : DEFAULT_THEME_MODE;

    return isThemeMode(parsed) ? parsed : DEFAULT_THEME_MODE;
  } catch (error) {
    console.warn(`Error reading localStorage key "${THEME_MODE_STORAGE_KEY}":`, error);
    return DEFAULT_THEME_MODE;
  }
}

export function ThemeModePreferenceProvider({ children, themes = appThemes }: ThemeModePreferenceProviderProps) {
  const [themeMode, setStoredThemeMode] = useState<ThemeMode>(readStoredThemeMode);
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const resolvedThemeMode =
    themeMode === THEME_MODES.SYSTEM ? (prefersDarkMode ? THEME_MODES.DARK : THEME_MODES.LIGHT) : themeMode;
  const selectedTheme = themes[resolvedThemeMode];

  const setThemeMode = useCallback((nextThemeMode: ThemeMode) => {
    try {
      window.localStorage.setItem(THEME_MODE_STORAGE_KEY, JSON.stringify(nextThemeMode));
    } catch (error) {
      console.warn(`Error setting localStorage key "${THEME_MODE_STORAGE_KEY}":`, error);
    }

    setStoredThemeMode(nextThemeMode);
  }, []);

  useEffect(() => {
    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key !== null && event.key !== THEME_MODE_STORAGE_KEY) {
        return;
      }

      setStoredThemeMode(readStoredThemeMode());
    };

    window.addEventListener('storage', handleStorageEvent);

    return () => window.removeEventListener('storage', handleStorageEvent);
  }, []);

  const contextValue = useMemo(
    () => ({
      themeMode,
      resolvedThemeMode,
      setThemeMode,
    }),
    [setThemeMode, themeMode, resolvedThemeMode],
  );

  return (
    <ThemeModePreferenceContext.Provider value={contextValue}>
      <ThemeProvider theme={selectedTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModePreferenceContext.Provider>
  );
}

export default function useThemeModePreference() {
  return useContext(ThemeModePreferenceContext);
}
